import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useWebsiteData } from '../../context/WebsiteDataContext';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { services, settings } = useWebsiteData();
  const location = useLocation();
  const navigate = useNavigate();

  const footerData = settings?.footer || {};
  
  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/#services' },
    { name: 'Projects', path: '/projects' },
    { name: 'Calculator', path: '/#calculator' },
    { name: 'Blog', path: '/#blog' },
    { name: 'About Us', path: '/aboutUs' },
  ];

  const handleQuickLinkClick = (e, link) => {
    const targetPath = link.path.split('#')[0];
    const targetHash = link.path.split('#')[1];

    if (location.pathname === targetPath) {
      if (targetHash) {
        e.preventDefault();
        const element = document.getElementById(targetHash);
        if (element) {
          const headerHeight = 72;
          const rect = element.getBoundingClientRect();
          const elementPosition = rect.top + window.scrollY - headerHeight;
          window.scrollTo({
            top: elementPosition,
            behavior: 'smooth'
          });
        }
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }
  };

  const categoryMap = {
    consultants: 'Professional Consultants',
    regulatory: 'Regulatory/Documentation',
    tools: 'Tools & Calculators',
    contractors: 'Contractors'
  };

  // Get unique categories from active services
  const footerCategories = Array.from(
    new Set(
      services
        .filter(s => s.status === 'active' && s.category)
        .map(s => s.category)
    )
  ).map(catId => categoryMap[catId] || catId.charAt(0).toUpperCase() + catId.slice(1));

  // Fallback categories if none in DB
  const displayCategories = footerCategories.length > 0 
    ? footerCategories 
    : [
        'Professional Consultants',
        'Regulatory/Documentation',
        'Tools & Calculators',
        'Contractors'
      ];

  const tools = [
    'FSI Calculator'
  ];

  const contactInfo = [
    { icon: 'MapPin', text: footerData?.contactInfo?.address || settings?.general?.address || 'Pune, Maharashtra, India' },
    { icon: 'Phone', text: footerData?.contactInfo?.phone || settings?.general?.contactPhone || '+91 80039 89393' },
    { icon: 'Mail', text: footerData?.contactInfo?.email || settings?.general?.contactEmail || 'nirmitee.associates2919@gmail.com' },
    { icon: 'Clock', text: footerData?.contactInfo?.hours || settings?.general?.workingHours || 'Mon-Sat: 9:00 AM - 7:00 PM' }
  ];

  // Dynamic social links from admin (array format)
  const socialLinks = Array.isArray(footerData?.socialLinks)
    ? footerData.socialLinks.filter(s => s.href)
    : [];

  return (
    <footer className="bg-primary text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-white rounded-construction flex items-center justify-center overflow-hidden p-1">
                <img src="/favicon.ico" alt="Nirmitee Logo" className="w-full h-full object-contain" />
              </div>
              <div>
                <div className="font-headline font-bold text-xl">Nirmitee</div>
                <div className="font-body text-xs text-white/80 -mt-1">ASSOCIATES</div>
              </div>
            </Link>

            <p className="text-white/80 text-sm mb-6 leading-relaxed">
              {footerData?.companyDescription || settings?.general?.siteDescription || "Pune's definitive construction intelligence platform, transforming complex bureaucratic processes into streamlined digital experiences."}
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              {contactInfo.map((item, index) => (
                <div key={index} className="flex items-center space-x-3 text-sm text-white/80">
                  <Icon name={item.icon} size={16} className="text-secondary" />
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-headline font-semibold text-lg mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.path}
                    onClick={(e) => handleQuickLinkClick(e, link)}
                    className="text-white/80 hover:text-secondary transition-construction text-sm flex items-center space-x-2 group"
                  >
                    <Icon name="ChevronRight" size={14} className="group-hover:translate-x-1 transition-construction" />
                    <span>{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-headline font-semibold text-lg mb-6">Our Services</h3>
            <ul className="space-y-3">
              {displayCategories.map((category, index) => (
                <li key={index} className="text-white/80 text-sm flex items-center space-x-2">
                  <Icon name="CheckCircle" size={14} className="text-accent" />
                  <span>{category}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Smart Tools */}
          <div>
            <h3 className="font-headline font-semibold text-lg mb-6">Smart Tools</h3>
            <ul className="space-y-3">
              {tools.map((tool, index) => (
                <li key={index} className="text-white/80 text-sm flex items-center space-x-2">
                  <Icon name="Calculator" size={14} className="text-accent" />
                  <span>{tool}</span>
                </li>
              ))}
            </ul>

            {/* Newsletter Signup */}
            <div className="mt-8">
              <h4 className="font-semibold text-white mb-3">Stay Updated</h4>
              <div className="flex">
                <input
                  type="email"
                  placeholder="Your email"
                  className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded-l-lg text-white placeholder-white/60 text-sm focus:outline-none focus:border-secondary"
                />
                <button className="px-4 py-2 bg-secondary hover:bg-secondary/90 rounded-r-lg transition-construction">
                  <Icon name="Send" size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Social Links & Certifications */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Social Links — only rendered when at least one link exists */}
            {socialLinks.length > 0 && (
              <div className="flex items-center space-x-4">
                <span className="text-white/80 text-sm mr-2">Follow us:</span>
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="w-10 h-10 bg-white/10 hover:bg-secondary rounded-full flex items-center justify-center transition-construction group"
                  >
                    <Icon name={social.icon} size={18} className="text-white group-hover:scale-110 transition-construction" />
                  </a>
                ))}
              </div>
            )}

            {/* Certifications */}
            <div className="flex items-center space-x-6 text-white/60 text-xs">
              <div className="flex items-center space-x-2">
                <Icon name="Award" size={16} />
                <span>ISO Certified</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={16} />
                <span>Government Registered</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Users" size={16} />
                <span>CREDAI Member</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 bg-primary/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/60">
            <div className="flex items-center space-x-4">
              <span>© {currentYear} Nirmitee Associates. All rights reserved.</span>
            </div>

            <div className="flex items-center space-x-6">
              <Link to="/privacy-policy" className="hover:text-white transition-construction">Privacy Policy</Link>
              <Link to="/terms-of-service" className="hover:text-white transition-construction">Terms of Service</Link>
              <Link to="/cookie-policy" className="hover:text-white transition-construction">Cookie Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;